import React, { useState } from 'react';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin, MessageCircle, X } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isFabOpen, setIsFabOpen] = useState(false);

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Collections', href: '#collections' },
    { name: 'Why Choose Us', href: '#why-choose-us' },
    { name: 'Contact', href: '#contact' }
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/closet_bytejaswi?igsh=cmR5Y210bWlvbG84&utm_source=qr', label: 'Instagram' },
    { icon: Facebook, href: 'https://facebook.com/closetbytejaswi', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com/closetbytejaswi', label: 'Twitter' }
  ];

  return (
    <footer className="bg-gray-900 text-white pt-16 md:pt-20 pb-8 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-12 md:mb-16">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex-shrink-0">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Closetbytejaswi
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">London, United Kingdom</span>
              </div>
              <p className="text-xs md:text-sm text-gray-400 mt-2 italic px-1">London&apos;s Contemporary Fashion</p>
            </div>
            <p className="text-sm md:text-base text-gray-300 leading-relaxed max-w-sm">
              Bringing contemporary Indian fashion to London with elegant designs, premium fabrics, and expert styling for all occasions.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 p-2.5 rounded-full hover:bg-purple-600 transition-all duration-300 shadow-sm"
                    aria-label={social.label}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div className="sm:pl-4 lg:pl-10">
            <h3 className="text-lg md:text-xl font-bold mb-6 md:mb-8 border-b border-gray-800 pb-2">Quick Links</h3>
            <ul className="grid grid-cols-2 sm:grid-cols-1 gap-3 md:gap-4 text-sm md:text-base">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.href} className="text-gray-300 hover:text-purple-400 transition-colors duration-200">
                    {link.name}
                  </a>
                </li>
              ))}
              <li><a href="#" className="text-gray-300 hover:text-purple-400 transition-colors duration-200">Size Guide</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:pl-5">
            <h3 className="text-lg md:text-xl font-bold mb-6 md:mb-8 border-b border-gray-800 pb-2">Contact Us</h3>
            <ul className="space-y-4 md:space-y-6 text-sm md:text-base">
              <li className="flex items-start gap-4">
                <div className="bg-purple-600/20 p-2.5 rounded-lg shrink-0">
                  <MapPin size={18} className="text-purple-400" />
                </div>
                <span className="text-gray-300">Harlow, Essex CM20, London, UK</span>
              </li>
              <li className="flex items-center gap-4">
                <div className="bg-purple-600/20 p-2.5 rounded-lg shrink-0">
                  <Phone size={18} className="text-purple-400" />
                </div>
                <span className="text-gray-300">+44 7721 540241</span>
              </li>
              <li className="flex items-center gap-4">
                <div className="bg-purple-600/20 p-2.5 rounded-lg shrink-0">
                  <Mail size={18} className="text-purple-400" />
                </div>
                <span className="text-gray-300 break-all">info@closetbytejaswi.co.uk</span>
              </li>
            </ul>
          </div>

          {/* Newsletter / CTA */}
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-6 md:mb-8 border-b border-gray-800 pb-2">Newsletter</h3>
            <p className="text-sm md:text-base text-gray-300 mb-6">
              Subscribe to get updates on new collections and special offers.
            </p>
            <form className="space-y-3">
              <input
                type="email"
                placeholder="Your email address"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-purple-600 transition-colors shadow-sm"
                required
              />
              <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all duration-300 shadow-md active:scale-95">
                Subscribe Now
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-center">
            <div className="space-y-1 order-2 md:order-1">
              <p className="text-xs md:text-sm text-gray-400">
                © {currentYear} Closetbytejaswi. All rights reserved.
              </p>
            </div>

            <div className="text-xs md:text-sm text-gray-400 order-1 md:order-2 flex flex-col items-center md:items-end gap-3 px-4">
              <p className="font-medium">
                Designed and Developed by{' '}
                <a
                  href="https://www.vikrin.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-pink-400 transition-colors border-b border-transparent hover:border-pink-400 pb-0.5"
                >
                  Vikrin
                </a>
              </p>
              <div className="flex items-center gap-4 text-gray-500 text-[10px] md:text-xs">
                <a href="#" className="hover:text-purple-400 transition-colors uppercase tracking-widest">Privacy Policy</a>
                <span className="w-1 h-1 bg-gray-700 rounded-full"></span>
                <a href="#" className="hover:text-purple-400 transition-colors uppercase tracking-widest">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Menu */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-4">
        {/* Toggleable Buttons */}
        <div
          className={`flex flex-col gap-3 transition-all duration-300 origin-bottom items-center
            ${isFabOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        >
          {/* Instagram Button */}
          <a
            href="https://www.instagram.com/closet_bytejaswi?igsh=cmR5Y210bWlvbG84&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3.5 rounded-full shadow-xl hover:scale-110 transition-transform duration-200"
            aria-label="Instagram"
          >
            <Instagram size={24} />
          </a>

          {/* WhatsApp Button */}
          <a
            href="https://wa.me/447721540241"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 text-white p-3.5 rounded-full shadow-xl hover:scale-110 transition-transform duration-200"
            aria-label="WhatsApp"
          >
            <svg className="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </a>
        </div>

        {/* Main Floating Button */}
        <button
          onClick={() => setIsFabOpen(!isFabOpen)}
          className={`bg-gray-900 text-white p-4 rounded-full shadow-2xl hover:shadow-gray-900/20 transition-all duration-300 z-50 flex items-center justify-center`}
          aria-label="Toggle contact menu"
        >
          {isFabOpen ? (
            <X size={28} className="transform rotate-90 transition-transform duration-300" />
          ) : (
            <MessageCircle size={28} className="transition-transform duration-300" />
          )}
        </button>
      </div>
    </footer>
  );
};

export default Footer;
